import React from 'react';
import {
  Box,
  HealthPlanStatuses,
  Inscription,
  PayoutRuleType,
  ServiceConfig,
  TierItem,
} from '@common-fe/common-fe';

import { HorizontalLine } from '@/components/elements';
import { TriggerCategory } from '@/modules/HealthPlan/HealthPlan.types.ts';
import ServiceConfigItem from '@/modules/HealthPlan/HealthPlanSetup/ComplexConfiguration/EnrollmentMatrix/Tiers/ServiceConfig/ServiceConfigItem.tsx';

import { LockCardPurseCard } from './LockCardPurseCard';
import { PayoutRulesCard } from './PayoutRulesCard';
import { TriggerAmount } from './TriggerAmount';
import { TriggerNotification } from './TriggerNotification';

interface TierBlockProps {
  tier: TierItem;
  nextTier?: TierItem;
  numberOfTier?: string;
  isLastItem?: boolean;
  healthPlanStatus?: HealthPlanStatuses;
  serviceConfigs?: ServiceConfig[];
  triggerAmounts?: Record<string, number>;
  payoutRuleType?: PayoutRuleType;
  planIdsForCardPurseLock?: string[];
  planNamesForCardPurseLock?: string[];
  isEmbeddedOnly?: boolean;
}

export const getTriggerName = (triggerCategoryType?: string) => {
  if (triggerCategoryType === TriggerCategory.FAMILY) {
    return 'Family';
  }
  if (triggerCategoryType === TriggerCategory.EMBEDDED) {
    return 'Individual';
  }
  return '';
};

export const TierBlock: React.FC<TierBlockProps> = ({
  tier,
  nextTier,
  numberOfTier,
  isLastItem,
  serviceConfigs,
  healthPlanStatus,
  triggerAmounts = {},
  payoutRuleType,
  planIdsForCardPurseLock = [],
  planNamesForCardPurseLock = [],
  isEmbeddedOnly,
}) => {
  return (
    <Box data-testid={`tier-block_${numberOfTier}`}>
      <Box
        pad={{ vertical: 'm', horizontal: 'l' }}
        round="container1Round"
        background="canvas"
        border={{ color: 'border2', size: '1px' }}
      >
        <Inscription color="textBody" size="xxlarge" weight={400} lineHeight="28px" margin={{ bottom: 'm' }}>
          {tier?.name || ''}
        </Inscription>
        <Box direction="row" gap="l">
          <PayoutRulesCard payoutRuleType={payoutRuleType} />
          {!isLastItem && !isEmbeddedOnly && (
            <LockCardPurseCard
              planIdsForCardPurseLock={planIdsForCardPurseLock}
              planNamesForCardPurseLock={planNamesForCardPurseLock}
            />
          )}
        </Box>
        {tier?.tierTriggers?.length ? <HorizontalLine margin={{ vertical: 'spacing16' }} /> : null}

        {tier?.tierTriggers?.length ? (
          <Box direction="row" gap="l">
            <Box width="35%">
              <Inscription color="textBody" size="large" lineHeight="22px" margin={{ bottom: 's' }}>
                Deductible met notification
              </Inscription>
              <Box background="module" pad="s" round="container1Round">
                <TriggerNotification isSendTriggerNotification={tier?.isSendTriggerNotification} />
              </Box>
            </Box>
            <Box width="65%">
              <Inscription color="textBody" lineHeight="22px" size="large" margin={{ bottom: 's' }}>
                {tier?.name || ''} to {nextTier?.name || ''}
              </Inscription>
              <Box background="module" pad="s" round="container1Round" gap="xs">
                {tier?.tierTriggers?.map((trigger) => (
                  <TriggerAmount
                    key={trigger.id}
                    triggerName={getTriggerName(trigger.triggerCategoryType)}
                    triggerAmount={triggerAmounts[trigger.id ?? ''] || 0}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        ) : null}
      </Box>
      {serviceConfigs
        ?.filter((service) => service?.processingTierId === tier?.id)
        ?.map((item) => (
          <ServiceConfigItem key={item?.id} value={item} hideOptions healthPlanStatus={healthPlanStatus} />
        ))}
      {!isLastItem && <Box height="1px" margin={{ vertical: 'm' }} background={{ color: 'border1' }} />}
    </Box>
  );
};
